/* eslint-disable */
import { get } from 'lodash';
import { mapActions, mapState } from 'vuex';
import Contract from '@/models/contract';
import marketChangePrice from '@/components/market/market-change-price/market-change-price.vue';
import orderBook from '@/components/order-book/order-book.vue';
import keyboardNumeric from '@/components/keyboard-numeric/keyboard-numeric.vue';
import keyboardNumericInput from '@/components/keyboard-numeric-input/keyboard-numeric-input.vue';
import purchaseYesNoSwitcher from '@/components/purchase/purchase-yes-no-switcher/purchase-yes-no-switcher.vue';
import purchaseOfferValue from '@/components/purchase/purchase-offer-value/purchase-offer-value.vue';
import purchaseQuantityValue from '@/components/purchase/purchase-quantity-value/purchase-quantity-value.vue';
import { capitalizeFirstLetter, isContractOpen, isMarketClosed } from '@/utils/helpers';
import { formMixin } from '@/components/forms/mixin';
import loaderMixin from '@/mixins/loader';
import constants from '@/constants';
import { MODALS } from '@/components/app/app-modals/app-modals';

export default {
  mixins: [
    formMixin,
    loaderMixin,
  ],
  components: {
    marketChangePrice,
    keyboardNumeric,
    keyboardNumericInput,
    orderBook,
    purchaseYesNoSwitcher,
    purchaseOfferValue,
    purchaseQuantityValue,
  },
  props: {
    market: {},
    contract: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState('trade', ['tradeOptions', 'cancelToggle', 'confirmationToggle']),
    classModifiers() {
      return {
        'purchase-offer': true,
        [`purchase-offer--${this.$router.currentRoute.name}`]: true,
      };
    },
    contractModel() {
      return new Contract(this.contract);
    },
    contractName() {
      if (!this.market || !this.contract) return '';
      const name = this.market.marketType === 0 ?
        this.tradeOptions.operationValue : this.contractModel.contractName;
      return capitalizeFirstLetter(name);
    },
    minQuantityValue() {
      if (this.tradeOptions.operationType === 'buy') {
        return (this.contractModel.userQuantity >= this.minimumOwnership)
          ? 1
          : Math.min(this.minimumOwnership, this.minimumOwnership - this.contractModel.userQuantity);
      } else {
        return (this.contractModel.userQuantity === 0)
          ? 1
          : (this.contractModel.userQuantity > this.minimumOwnership)
            ? 1
            : this.contractModel.userQuantity;
      }
    },
    maxQuantityValue() {
      if (this.tradeOptions.operationType === 'buy') {
        return 9999;
      } else {
        return (this.contractModel.userQuantity === 0)
          ? 1
          : this.contractModel.userQuantity;
          // CR
          // : (this.contractModel.userQuantity > this.minimumOwnership)
          //   ? this.contractModel.userQuantity - this.minimumOwnership
          //   : this.contractModel.userQuantity;
      }
    },
    minimumOwnership() {
      return Math.max(get(this.contract, 'minimumClipSize', 1), 1);
    },
    showThresholdInfo() {
      return this.minimumOwnership > 1;
    },
    initialPricePerShare() {
      let initialPrice = 0;
      // buy
      if (this.tradeOptions.operationType === 'buy') {
        if (this.tradeOptions.operationValue === 'yes') {
          initialPrice = this.contractModel.buyYesCostAsCents;
        } else {
          initialPrice = this.contractModel.buyNoCostAsCents;
        }
      } else if (this.tradeOptions.operationValue === 'yes') { // sell
        initialPrice = this.contractModel.sellYesCostAsCents;
      } else {
        initialPrice = this.contractModel.sellNoCostAsCents;
      }

      if (initialPrice === 0) {
        initialPrice = this.contractModel.lastTradePriceAsCents;
        if (initialPrice > 0 && this.tradeOptions.operationValue === 'no') {
          initialPrice = 100 - initialPrice;
        }
      }

      if (initialPrice === 0) {
        initialPrice = 50;
      }

      return initialPrice;
    },
    mainClassName() {
      return 'purchase-offer';
    },
    classSharesOwnedLabel() {
      return {
        [`${this.mainClassName}__offer-table-contract-shares-owned--no`]:
          this.contractModel.mode === 'no',
        [`${this.mainClassName}__offer-table-contract-shares-owned--yes`]:
          this.contractModel.mode === 'yes',
      };
    },
    quantityInputClass() {
      return {
        [`${this.mainClassName}__offer-quantity-input`]: true,
        [`${this.mainClassName}__offer-quantity-input--empty`]: !this.tradeOptions.quantity,
        [`${this.mainClassName}__offer-quantity-input--error`]: this.quantityError,
        [`${this.mainClassName}__offer-quantity-input--focus`]: this.quantityFocused,
      };
    },
    quantitySelectedClass() {
      return {
        'offer-quantity--selected': this.keyboardNumericType === 'quantity',
      };
    },
    quantityErrorText() {
      const { reduced, min, max } = this.getReducedQuantity()
      // console.log(`DEBUG reduced=${reduced}, minimumOwnership=${this.minimumOwnership}, quantity=${this.quantity}, max=${max}`)

      if (this.tradeOptions.operationType === 'buy' && this.quantity && this.quantity < reduced) {
        return `You must own at least ${min} shares in this contract`
      }
      if (this.tradeOptions.operationType === 'sell' && this.quantity > this.contractModel.userQuantity) {
        if (this.contractModel.userQuantity === 1) {
          return 'You have only 1 share to sell';
        }
        if (this.contractModel.userQuantity === 0) {
          return "You don't have any shares to sell";
        }
        return `You have only ${this.contractModel.userQuantity} shares to sell`;
      }
      if (this.tradeOptions.operationType === 'sell' && this.quantity && this.quantity < reduced) {
        return `You must maintain ownership of at least ${min} shares`
      }
      // CR
      if (this.tradeOptions.operationType === 'sell' && this.quantity && max && (this.quantity > max && this.quantity !== this.contractModel.userQuantity)) {
        return `You must maintain ownership of at least ${min} shares`
      }
      return null;
    },
    isPriceValid() {
      if (this.pricePerShare === null || isNaN(this.pricePerShare) || this.pricePerShare === 0) {
        return false;
      }
      return true;
    },
    isQuantityValid() {
      if (this.quantity === null) return false;
      if (this.tradeOptions.operationType === 'sell') {
        if (this.quantity > this.contractModel.userQuantity) return false;
      }
      return !this.quantityErrorText;
    },
    purchaseOfferTitle() {
      const prefix = (this.tradeOptions.operationType === 'buy') ? 'Maximum' : 'Minimum';
      return `${prefix} ${this.tradeOptions.operationType} Price`;
    },
    nextButtonTitle() {
      return (this.tradeOptions && this.tradeOptions.skipConfirmation) ? 'Submit Offer' : 'Next';
    },
    nextButtonDisabled() {
      return isMarketClosed(this.market) ||
        !this.isQuantityValid ||
        !this.isPriceValid ||
        this.isLoading;
    },
    purchaseOfferKey() {
      const timestamp = this.contractModel.timestamp || '-';
      return `${this.contractModel.contractId}-${timestamp}-${this.contractModel.mode}-${this.contractModel.userOpenOrders}-${this.tradeOptions.operationType}-${this.tradeOptions.operationValue}`;
    },
    orderBookKey() {
      return this.purchaseOfferKey;
    },
    isContractOpen() {
      return isContractOpen(this.contract);
    },
  },
  data() {
    return {
      isMarketClosed,
      pricePerShareUpdateCount: 0,
      pricePerShare: 0,
      quantity: constants.DEFAULT_QUANTITY,
      skipConfirmation: false,
      keyboardNumericType: null,
      quantityError: null,
      quantityFocused: false,
      priceFocused: false,
    };
  },
  watch: {
    'tradeOptions.operationType': function watchOperationType() {
      this.pricePerShareUpdateCount += 1;
      this.pricePerShare = this.getDefaultPricePerShare();
      this.setDefaultQuantity();
    },
    'tradeOptions.operationValue': function watchOperationValue() {
      this.pricePerShareUpdateCount += 1;
      this.pricePerShare = this.getDefaultPricePerShare();
      this.setDefaultQuantity();
    },
    contractModel() {
      // reinit contract after offer cancelation
      if (this.cancelToggle) {
        this.setCancelToggle(false);
        // this.pricePerShare = this.getDefaultPricePerShare();
        // this.quantity = this.getDefaultQuantity();
        // if (!(this.keyboardNumericType || this.quantityFocused || this.priceFocused)) {
        //   this.pricePerShare = this.initialPricePerShare;
        //   this.quantity = constants.DEFAULT_QUANTITY;
        // }
      }
    },
    pricePerShare(value) {
      this.setTradePrice(value);
      this.pricePerShare = this.tradeOptions.pricePerShare;
    },
    quantity(value) {
      this.setTradeQuantity(value);
      this.quantity = this.tradeOptions.quantity;
    },
    skipConfirmation(value) {
      this.setTradeSkipConfirmation(value);
      this.skipConfirmation = this.tradeOptions.skipConfirmation;
    },
  },
  methods: {
    ...mapActions('market', ['getMarketRules']),
    ...mapActions('trade', [
      'setTradeQuantity', 'setTradePrice',
      'setTradeSkipConfirmation', 'setCancelToggle',
      'setConfirmationToggle',
    ]),
    ...mapActions('popup', ['setGenericSmallPopup', 'setCurrentSmallPopup', 'setCurrentPopupOptions']),
    openThresholdInfoPopup() {
      this.setCurrentSmallPopup(MODALS.SMALL_OWNERSHIP_INFO);
      this.setCurrentPopupOptions({ shares: this.minimumOwnership });
    },
    getDefaultPricePerShare() {
      return this.initialPricePerShare;
    },
    getReducedQuantity() {
      if (this.tradeOptions.operationType === 'sell') {
        return (this.contractModel.userQuantity === 0)
          ? {
            reduced: 1,
            min: this.minimumOwnership,
            max: null
          }
          : (this.contractModel.userQuantity > this.minimumOwnership)
            ? {
              reduced: 1,
              min: this.minimumOwnership,
              max: this.contractModel.userQuantity - this.minimumOwnership
            }
            : {
              reduced: this.contractModel.userQuantity,
              min: this.minimumOwnership,
              max: null
            }
      } else if (this.tradeOptions.operationType === 'buy') {
        return (this.contractModel.userQuantity >= this.minimumOwnership)
          ? {
            reduced: 1,
            min: this.minimumOwnership,
            max: null,
          }
          : {
            reduced: Math.min(this.minimumOwnership, this.minimumOwnership - this.contractModel.userQuantity),
            min: this.minimumOwnership,
            max: null,
          }
      }
    },
    setDefaultQuantity() {
      const { reduced, min } = this.getReducedQuantity()
      // console.log(`DEBUG set default: ${reduced}`)
      this.quantity = reduced
      this.setTradeQuantity(this.quantity);
    },
    onUpdatePriceAndQuantity(data) {
      this.setTradeQuantity(Math.min(data.quantity, 9999));
      this.quantity = this.tradeOptions.quantity;
      this.setTradePrice(data.price);
      this.pricePerShare = this.tradeOptions.pricePerShare;
    },
    onClickQuantityInput() {
      this.keyboardNumericType = 'quantity';
    },
    onClickPricePerShareInput() {
      this.keyboardNumericType = 'pricePerShare';
    },
    onOutsideClick() {
      this.keyboardNumericType = null;
    },
    onClickNext() {
      if (this.isQuantityValid && !this.isLoading) {
        this.$emit('submit');
      } else {
        this.quantityError = true;
      }
    },
    onReadRules() {
      if (this.$router.currentRoute.name === 'portfolio') {
        this.loaderAdd(document.body);
        this.getMarketRules(this.market.marketId)
          .then((data) => {
            this.loaderRemove(document.body);
            this.setGenericSmallPopup({
              name: MODALS.SMALL_MARKET_RULES,
              options: {
                title: 'The Rules',
                content: data.rule,
              },
            });
          })
          .catch(() => this.loaderRemove(document.body));
      } else {
        this.setGenericSmallPopup({
          name: MODALS.SMALL_MARKET_RULES,
          options: {
            title: 'The Rules',
            content: this.market.rule,
          },
        });
      }
    },
    onQuantityInputFocus() {
      this.quantityFocused = true;
    },
    onQuantityInputBlur() {
      this.quantityFocused = false;
      // console.log(`DEBUG minQuantityValue=${this.minQuantityValue} maxQuantityValue=${this.maxQuantityValue}`)

      // if (this.quantity === null || this.quantity < this.minQuantityValue) {
      //   this.quantity = this.minQuantityValue;
      // } else if (this.quantity > this.maxQuantityValue) {
      //   this.quantity = this.maxQuantityValue
      // }
    },
    onPriceFocus() {
      this.priceFocused = true;
    },
    onPriceBlur() {
      this.priceFocused = false;
    },
    onClickQuantityBox() {
      this.$refs.sharesNumberInput.click();
    },
  },
  created() {
    // do not reinit contract if user comes back from confirm page
    if (this.confirmationToggle && this.tradeOptions.pricePerShare !== null) {
      this.quantity = this.tradeOptions.quantity;
      this.setConfirmationToggle(false);
    } else {
      this.setTradePrice(this.getDefaultPricePerShare());
      this.setDefaultQuantity();
    }

    this.pricePerShare = this.tradeOptions.pricePerShare;
    // this.quantity = (defaultQuantity === null) ? defaultQuantity : this.tradeOptions.quantity;
    this.skipConfirmation = this.tradeOptions.skipConfirmation;
  },
};



// WEBPACK FOOTER //
// ./src/components/purchase/purchase-offer/purchase-offer.js